import { Children, ReactNode } from "react";
import { Box } from "@mui/material";

type TGridSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

interface IDashboardSectionRowProps {
  // Ancho (sobre 12) de cada hijo directo en desktop (md+), en el mismo orden
  // que los children. En xs siempre ocupan el 100% (se apilan).
  widths: TGridSize[];
  spacing?: number;
  children: ReactNode;
}

// Reemplaza los `Grid container` con anchos hardcodeados repetidos en
// MetricsDashboard.tsx: hace explícitas las proporciones de una fila de
// sub-secciones "hermanas" (ej. 33/33/33, 66/33) en un solo lugar por grupo.
// No sabe nada de loading/error/título: cada hijo sigue resolviendo eso con
// su propio DashboardSectionContainer.
//
// CSS Grid nativo (mismo patrón que ExecutiveSummary.tsx) en vez de MUI
// <Grid>: el Grid legacy simula el gap con margin negativo en el
// `container`, lo que obliga a contener el desborde envolviendo todo en un
// Box con overflow — y ese overflow, a su vez, puede recortar contenido
// (ej. el border-bottom de una tarjeta) si el alto automático se ve
// afectado por ese mismo margen negativo en el eje Y. `gap` de CSS Grid es
// un gutter real: sin márgenes negativos, no hay nada que contener.
const DashboardSectionRow = ({
  widths,
  spacing = 3,
  children,
}: IDashboardSectionRowProps) => {
  const items = Children.toArray(children);
  const columns = widths.map((width) => `${width}fr`).join(" ");

  return (
    <Box
      sx={{
        display: "grid",
        gap: spacing,
        gridTemplateColumns: { xs: "1fr", md: columns },
      }}
    >
      {items.map((child, index) => (
        // minWidth: 0 es necesario porque un item de grid, igual que uno
        // de flex, por default no se achica por debajo del ancho de su
        // contenido: sin esto, una tabla ancha (ej. el ranking de
        // Geography) se sale de su columna y se superpone con la sección
        // vecina en vez de scrollear internamente.
        <Box key={index} sx={{ minWidth: 0 }}>
          {child}
        </Box>
      ))}
    </Box>
  );
};

export default DashboardSectionRow;
